/**
Copyright (c) 2022–2026 Podstack.ai
All rights reserved.
Proprietary and Confidential.
Unauthorized copying of this file, via any medium, is strictly prohibited.
This file is part of the Podstack Platform.
*/

import React, { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import toast from 'react-hot-toast';
import { walletService } from '../../services/api';
import type { RefundDetailedBreakdown, RefundEligibility, RefundResponse } from '../../types';
import { Button, KV, Skeleton, Tag } from '../console/ui';
import BillingModal, { Notice } from './BillingModal';
import { downloadRefundBreakdownCSV } from './billingUtils';
import { useInvalidateWallet } from './useBilling';

const inr = (n?: number) => `₹${(n || 0).toFixed(2)}`;

const Field: React.FC<{ label: string; required?: boolean; hint?: string; children: React.ReactNode }> = ({
  label,
  required,
  hint,
  children,
}) => (
  <label className="flex flex-col gap-1.5 text-sm">
    <span className="text-ps-muted text-[13px]">
      {label}
      {required && <span className="text-ps-err"> *</span>}
    </span>
    {children}
    {hint && <span className="text-xs text-ps-subtle">{hint}</span>}
  </label>
);

/** Refund request (UPI / bank) with FIFO eligibility breakdown. */
const RefundModal: React.FC<{ open: boolean; onClose: () => void }> = ({ open, onClose }) => {
  const invalidate = useInvalidateWallet();
  const [showDetails, setShowDetails] = useState(false);
  const [amount, setAmount] = useState(0);
  const [reason, setReason] = useState('');
  const [mode, setMode] = useState<'upi' | 'bank'>('upi');
  const [upiId, setUpiId] = useState('');
  const [bankAccount, setBankAccount] = useState('');
  const [bankIfsc, setBankIfsc] = useState('');
  const [bankName, setBankName] = useState('');

  const { data: eligibility } = useQuery({
    queryKey: ['refundEligibility'],
    queryFn: () => walletService.getRefundEligibility().then((res) => res.data as RefundEligibility),
    enabled: open,
  });

  const { data: details, isLoading: detailsLoading } = useQuery({
    queryKey: ['refundEligibilityDetails'],
    queryFn: () => walletService.getRefundEligibilityDetails().then((res) => res.data as RefundDetailedBreakdown),
    enabled: showDetails,
  });

  const reset = () => {
    setAmount(0);
    setReason('');
    setMode('upi');
    setUpiId('');
    setBankAccount('');
    setBankIfsc('');
    setBankName('');
  };

  const close = () => {
    reset();
    onClose();
  };

  const refundMutation = useMutation({
    mutationFn: (request: Parameters<typeof walletService.requestRefund>[0]) => walletService.requestRefund(request),
    onSuccess: (response) => {
      const data = response.data as RefundResponse;
      invalidate();
      close();
      toast.success(
        data.message || `Refund request submitted! Amount will be credited within ${data.estimated_days} working days.`,
        { duration: 8000 },
      );
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Failed to submit refund request');
    },
  });

  const max = eligibility?.refundable_amount || 0;
  const invalid =
    amount <= 0 ||
    amount > max ||
    refundMutation.isPending ||
    (mode === 'upi' && !upiId) ||
    (mode === 'bank' && (!bankAccount || !bankIfsc || !bankName));

  const submit = () => {
    const payment_mode = mode;
    refundMutation.mutate({
      amount,
      reason,
      payment_mode,
      upi_id: payment_mode === 'upi' ? upiId : undefined,
      bank_account_no: payment_mode === 'bank' ? bankAccount : undefined,
      bank_ifsc: payment_mode === 'bank' ? bankIfsc : undefined,
      bank_account_name: payment_mode === 'bank' ? bankName : undefined,
    });
  };

  return (
    <>
      <BillingModal open={open} title="Request a refund" onClose={close}>
        {!eligibility ? (
          <div className="flex flex-col gap-2">
            <Skeleton className="h-5" />
            <Skeleton className="h-5" />
            <Skeleton className="h-5" />
          </div>
        ) : (
          <>
            <div className="rounded-[10px] bg-ps-chrome p-4 flex flex-col gap-2.5">
              <KV k="Current balance" v={inr(eligibility.total_balance)} />
              <KV k="Paid via gateway" v={inr(eligibility.refundable_before_charge)} />
              {(eligibility.gateway_charge_amount || 0) > 0 && (
                <KV
                  k={`Gateway charge (${eligibility.gateway_charge_percent || 0}%)`}
                  v={<span className="text-ps-err">−{inr(eligibility.gateway_charge_amount)}</span>}
                />
              )}
              <div className="border-t border-ps-border pt-2">
                <KV k={<span className="text-ps-ok">Net refundable</span>} v={<span className="text-ps-ok">{inr(max)}</span>} />
              </div>
              {(eligibility.non_refundable_amount || 0) > 0 && (
                <KV k="Coupon credits (non-refundable)" v={inr(eligibility.non_refundable_amount)} />
              )}
              <button type="button" className="ps-link text-xs self-start mt-1" onClick={() => setShowDetails(true)}>
                How is this calculated? View details
              </button>
            </div>

            {(eligibility.non_refundable_amount || 0) > 0 && (
              <Notice>{eligibility.non_refundable_reason || 'Credits from coupon codes cannot be refunded.'}</Notice>
            )}

            {max > 0 ? (
              <>
                <Field label="Refund amount">
                  <input
                    type="number"
                    className="ps-input"
                    value={amount || ''}
                    min={1}
                    max={max}
                    step={0.01}
                    placeholder={`Max ${inr(max)}`}
                    onChange={(e) => setAmount(Math.min(parseFloat(e.target.value) || 0, max))}
                  />
                  <button type="button" className="ps-link text-xs self-start" onClick={() => setAmount(max)}>
                    Refund full amount ({inr(max)})
                  </button>
                </Field>

                <div className="flex gap-2" role="group" aria-label="Refund method">
                  {(['upi', 'bank'] as const).map((m) => (
                    <button
                      key={m}
                      type="button"
                      aria-pressed={mode === m}
                      onClick={() => setMode(m)}
                      className={`ps-chip ${mode === m ? 'ps-chip-on' : ''}`}
                    >
                      {m === 'upi' ? 'UPI' : 'Bank account'}
                    </button>
                  ))}
                </div>
                {mode === 'upi' ? (
                  <Field label="UPI ID" required hint="e.g. name@paytm, name@oksbi">
                    <input className="ps-input" value={upiId} onChange={(e) => setUpiId(e.target.value)} placeholder="yourname@upi" />
                  </Field>
                ) : (
                  <>
                    <Field label="Account holder name" required>
                      <input
                        className="ps-input"
                        value={bankName}
                        onChange={(e) => setBankName(e.target.value)}
                        placeholder="As per bank records"
                      />
                    </Field>
                    <Field label="Account number" required>
                      <input
                        className="ps-input"
                        inputMode="numeric"
                        value={bankAccount}
                        onChange={(e) => setBankAccount(e.target.value.replace(/\D/g, ''))}
                        placeholder="Enter account number"
                      />
                    </Field>
                    <Field label="IFSC code" required>
                      <input
                        className="ps-input"
                        value={bankIfsc}
                        onChange={(e) => setBankIfsc(e.target.value.toUpperCase())}
                        placeholder="e.g. SBIN0001234"
                        maxLength={11}
                      />
                    </Field>
                  </>
                )}
                <Field label="Reason (optional)">
                  <textarea
                    className="ps-input py-2 resize-none"
                    rows={2}
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    placeholder="Why are you requesting a refund?"
                  />
                </Field>
                <Notice tone="info">
                  Refunds are processed within 5–7 working days. You’ll get an email confirmation once submitted.
                </Notice>
                <Button onClick={submit} disabled={invalid}>
                  {refundMutation.isPending ? 'Submitting request…' : `Submit refund request · ${inr(amount)}`}
                </Button>
              </>
            ) : (
              <div className="text-center py-4 flex flex-col gap-1.5">
                <span className="text-ps-text">No refundable balance</span>
                <span className="text-sm text-ps-muted">
                  Only amounts paid via the payment gateway can be refunded. Coupon credits are non-refundable.
                </span>
              </div>
            )}
          </>
        )}
      </BillingModal>

      <BillingModal
        open={showDetails}
        title="Refund calculation details"
        onClose={() => setShowDetails(false)}
        wide
        z="z-[60]"
        actions={
          details ? (
            <Button variant="secondary" size="sm" onClick={() => downloadRefundBreakdownCSV(details)}>
              Download CSV
            </Button>
          ) : undefined
        }
      >
        {detailsLoading ? (
          <Skeleton className="h-40" />
        ) : details ? (
          <>
            <Notice tone="info">
              <p className="m-0">
                <b className="font-medium">FIFO order:</b> credits are consumed in the order they were added, oldest first.
              </p>
              <p className="m-0 mt-1.5">
                <b className="font-medium">Paid vs coupon:</b> only amounts paid via the payment gateway (UPI, card) can be
                refunded. Coupon and promotional credits are non-refundable.
              </p>
              <p className="m-0 mt-1.5">
                <b className="font-medium">Gateway charge:</b> a {details.gateway_charge_percent}% processing fee is deducted
                from refunds.
              </p>
            </Notice>
            <div className="rounded-[10px] bg-ps-chrome p-4 grid sm:grid-cols-2 gap-x-6 gap-y-2.5">
              <KV k="Current balance" v={inr(details.total_balance)} />
              <KV k="Total debits used" v={inr(details.total_debits)} />
              <KV k="Paid credits remaining" v={inr(details.refundable_before_charge)} />
              <KV
                k={`Gateway charge (${details.gateway_charge_percent}%)`}
                v={<span className="text-ps-err">−{inr(details.gateway_charge_amount)}</span>}
              />
              <div className="sm:col-span-2 border-t border-ps-border pt-2">
                <KV k={<span className="text-ps-ok">Net refundable</span>} v={<span className="text-ps-ok">{inr(details.refundable_amount)}</span>} />
              </div>
            </div>
            <h3 className="m-0 text-sm font-medium text-ps-text">Transaction breakdown (FIFO order)</h3>
            <div className="overflow-x-auto border border-ps-border rounded-[10px]">
              <table className="ps-tbl">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Description</th>
                    <th>Source</th>
                    <th className="!text-right">Amount</th>
                    <th className="!text-right">Consumed</th>
                    <th className="!text-right">Remaining</th>
                    <th className="!text-right">Refundable</th>
                  </tr>
                </thead>
                <tbody>
                  {details.transactions.map((tx, i) => (
                    <tr key={tx.id || i}>
                      <td className="text-ps-muted">{format(new Date(tx.date), 'd MMM yyyy')}</td>
                      <td className="max-w-[180px] truncate" title={tx.description}>
                        {tx.description}
                      </td>
                      <td>
                        <Tag tone={tx.is_paid ? 'ok' : 'accent'}>{tx.is_paid ? 'Gateway' : 'Coupon'}</Tag>
                      </td>
                      <td className="text-right">{inr(tx.amount)}</td>
                      <td className="text-right text-ps-err">{tx.consumed > 0 ? `−${inr(tx.consumed)}` : '-'}</td>
                      <td className="text-right text-ps-muted">{inr(tx.remaining)}</td>
                      <td className="text-right text-ps-ok">{tx.is_paid && tx.refundable > 0 ? inr(tx.refundable) : '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        ) : (
          <p className="m-0 text-ps-muted text-center py-8">Failed to load details</p>
        )}
      </BillingModal>
    </>
  );
};

export default RefundModal;
